import React, { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial, PerspectiveCamera, Stars, Text, PresentationControls, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';

const CyberDogVisuals = () => {
    const group = useRef<THREE.Group>(null);

    useFrame((state) => {
        if (!group.current) return;
        group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.2;
    });

    return (
        <group ref={group}>
            {/* Body */}
            <mesh position={[0, 0.3, 0]} castShadow>
                <boxGeometry args={[0.5, 0.5, 0.9]} />
                <meshStandardMaterial color="#1a1a2e" metalness={0.8} roughness={0.2} />
            </mesh>
            {/* Neon Stripes */}
            <mesh position={[0, 0.31, 0]} scale={[1.02, 1, 0.8]}>
                <boxGeometry args={[0.5, 0.5, 0.1]} />
                <meshStandardMaterial color="#00ffcc" emissive="#00ffcc" emissiveIntensity={2} toneMapped={false} />
            </mesh>

            {/* Head */}
            <mesh position={[0, 0.6, 0.5]} castShadow>
                <boxGeometry args={[0.35, 0.35, 0.4]} />
                <meshStandardMaterial color="#1a1a2e" metalness={0.8} roughness={0.2} />
            </mesh>

            {/* Visor/Eye */}
            <mesh position={[0, 0.6, 0.71]}>
                <planeGeometry args={[0.25, 0.05]} />
                <meshStandardMaterial color="#ff0055" emissive="#ff0055" emissiveIntensity={3} toneMapped={false} />
            </mesh>

            {/* Legs */}
            <mesh position={[0.2, 0.1, 0.3]}>
                <boxGeometry args={[0.1, 0.4, 0.1]} />
                <meshStandardMaterial color="#333" />
            </mesh>
            <mesh position={[-0.2, 0.1, 0.3]}>
                <boxGeometry args={[0.1, 0.4, 0.1]} />
                <meshStandardMaterial color="#333" />
            </mesh>
            <mesh position={[0.2, 0.1, -0.3]}>
                <boxGeometry args={[0.1, 0.4, 0.1]} />
                <meshStandardMaterial color="#333" />
            </mesh>
            <mesh position={[-0.2, 0.1, -0.3]}>
                <boxGeometry args={[0.1, 0.4, 0.1]} />
                <meshStandardMaterial color="#333" />
            </mesh>
        </group>
    );
};

const Scene = () => {
    return (
        <>
            <PerspectiveCamera makeDefault position={[0, 1, 5]} fov={50} />
            <Environment preset="city" />
            <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />

            <PresentationControls
                global
                rotation={[0, 0.3, 0]}
                polar={[-Math.PI / 3, Math.PI / 3]}
                azimuth={[-Math.PI / 1.4, Math.PI / 1.4]}
            >
                <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
                    <CyberDogVisuals />
                </Float>
            </PresentationControls>

            <ContactShadows resolution={1024} scale={20} blur={2} opacity={0.35} far={10} color="#000000" />

            {/* Background Glow */}
            <mesh position={[0, 0, -5]} scale={[20, 20, 1]}>
                <planeGeometry />
                <MeshDistortMaterial
                    color="#050510"
                    speed={2}
                    distort={0.4}
                    radius={1}
                />
            </mesh>

            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} color="#00ffcc" />
            <pointLight position={[-10, -10, -10]} intensity={0.5} color="#ff0055" />
        </>
    );
};

export const MaintenancePage: React.FC = () => {
    return (
        <div style={{
            width: '100vw',
            height: '100vh',
            background: '#050510',
            position: 'fixed',
            top: 0,
            left: 0,
            overflow: 'hidden',
            fontFamily: "'Inter', sans-serif",
            color: 'white'
        }}>
            <Canvas shadows gl={{ antialias: true, stencil: false, depth: true }}>
                <Suspense fallback={null}>
                    <Scene />
                </Suspense>
            </Canvas>

            {/* UI Overlay */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2rem'
            }}>
                <div style={{
                    padding: '1rem 2rem',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                }}>
                    <h1 style={{
                        fontSize: '3.5rem',
                        fontWeight: 900,
                        margin: 0,
                        letterSpacing: '0.5rem',
                        background: 'linear-gradient(90deg, #00ffcc, #4a90ff, #ff0055)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textTransform: 'uppercase'
                    }}>
                        Maintenance
                    </h1>
                </div>

                <p style={{
                    fontSize: '1.2rem',
                    maxWidth: '600px',
                    lineHeight: '1.6',
                    opacity: 0.7,
                    margin: 0,
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}>
                    We are currently upgrading the cyber-infrastructure.
                    The portfolio will be back online shortly with new features and improved performance.
                </p>

                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginTop: '1rem'
                }}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: i === 1 ? '#00ffcc' : i === 2 ? '#4a90ff' : '#ff0055',
                            animation: `pulse 1.5s infinite ${i * 0.2}s`
                        }} />
                    ))}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 0.5; }
                    50% { transform: scale(1.5); opacity: 1; }
                    100% { transform: scale(1); opacity: 0.5; }
                }
            ` }} />

            {/* Corner Accents */}
            <div style={{ position: 'absolute', top: '40px', left: '40px', fontSize: '12px', letterSpacing: '2px', opacity: 0.3 }}>
                SYSTEM_STATUS: OFFLINE
            </div>
            <div style={{ position: 'absolute', bottom: '40px', right: '40px', fontSize: '12px', letterSpacing: '2px', opacity: 0.3 }}>
                v2.0.4-BUILD_INIT
            </div>
        </div>
    );
};
